import React, { useState, useEffect } from 'react';
import { Phone, MessageCircle, Award, Users, Calendar, CheckCircle, MapPin, Menu, X, ArrowRight, ShieldCheck, Factory, ChevronDown, ChevronUp, Youtube, Play, Download, Instagram, Facebook } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext } from
"./components/ui/carousel";
import { Button } from './components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Sheet, SheetContent, SheetTrigger } from './components/ui/sheet';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOMESTIC_PDF = "/assets/Domestic_Almirah.pdf";
const OFFICE_PDF = "/assets/new_final_office_furniture_2.pdf";

const HERO_IMAGES = [
"/assets/49a943b4-4755-453f-b210-e9ee15cdeee8.png",
"/assets/ce314ca3-f2dd-4185-b363-a3421b9a02cc.png",
"/assets/7c1b37ff-1a3d-421c-acb2-f32061944ea7.png",
"/assets/915c92b6-de65-4218-8342-e3c77f514985.png"];


const CERT_LOGOS = [
"/assets/ef66c441-b3bf-4bb7-9e13-046c45a57572-removebg-preview.png",
"/assets/Certificazione-ISO-45001-2018-removebg-preview.png",
"/assets/image.png",
"/assets/iso.png",
"/assets/gem-government-e-marketplace-logo-hd.webp"];


const FEATURED_VIDEOS = [
"https://customer-assets-jai6qajn.emergentagent.net/job_ba7e396b-9a75-464f-8c68-146b03c425d5/artifacts/2o9cziit_RLS_Mahila_12.mp4",
"https://customer-assets-jai6qajn.emergentagent.net/job_ba7e396b-9a75-464f-8c68-146b03c425d5/artifacts/up9qt1nb_RLS_Gharghuti_8.mp4",
"/assets/Post_7.mp4._1.mp4",
"/assets/Post_3._1.mp4"];


const YOUTUBE_LINK = "https://youtube.com/@reckonfurnitures5959?si=Of69zA4j_LIkwFVJ";
const CONTACT_NUMBER = "+91 9373102545";
const WHATSAPP_LINK = `https://wa.me/91${CONTACT_NUMBER.replace(/\D/g, '').slice(-10)}`;

const NavLinks = ({ mobile = false, setIsMenuOpen }) => {
  const classes = mobile ?
  "flex flex-col space-y-4 text-lg font-medium text-royal-maroon" :
  "flex space-x-8 text-sm font-semibold tracking-wide text-gray-800 hover:text-royal-maroon transition-colors";

  return (
    <div className={classes}>
      <a href="#about" onClick={() => mobile && setIsMenuOpen(false)}>About Us</a>
      <a href="#products" onClick={() => mobile && setIsMenuOpen(false)}>Products</a>
      <a href="#contact" onClick={() => mobile && setIsMenuOpen(false)}>Contact</a>
    </div>);

};

const HERO_CAROUSEL_OPTS = { loop: true };
const VIDEO_CAROUSEL_OPTS = { align: "start", loop: true };
const CHART_TICK_STYLE = { fontSize: 12 };

export default function RoyalLanding() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });
  const [showSocials, setShowSocials] = useState(false);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('domestic');
  const [showAll, setShowAll] = useState(false);
  const [api, setApi] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`)]
        );
        setProducts(prodRes.data);
        setCategories(catRes.data);
      } catch (_err) {
        // Data fetch failed - products will show empty state
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 3000);
    return () => clearInterval(interval);
  }, [api]);

  const trackClick = async (type) => {
    try {
      await axios.post(`${API}/analytics/track`, { event_type: type });
    } catch (_e) {/* tracking is non-critical */}
  };

  const handleCall = () => {
    trackClick('call');
    window.location.href = `tel:${CONTACT_NUMBER.replace(/\s+/g, '')}`;
  };

  const handleWhatsApp = (msg = "") => {
    trackClick('whatsapp');
    const text = msg ? `?text=${encodeURIComponent(msg)}` : "";
    window.open(`${WHATSAPP_LINK}${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Thank you! Opening WhatsApp to complete your enquiry.");

      const msg = `Hi, I am ${formData.name}. I found you on www.royalsteelfurniture.in. I am interested in Royal Life Furniture. ${formData.message}`;
      handleWhatsApp(msg);

      setFormData({ name: '', phone: '', message: '' });
    } catch (_err) {
      toast.error("Something went wrong with saving your details, but we will open WhatsApp for you.");
      const msg = `Hi, I am ${formData.name}. I found you on www.royalsteelfurniture.in. I am interested in Royal Life Furniture. ${formData.message}`;
      handleWhatsApp(msg);
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on active tab
  const filteredProducts = products.filter((p) => p.category_slug === activeTab);
  const displayProducts = showAll ? filteredProducts : filteredProducts.slice(0, 6);

  return (
    <div className="min-h-screen bg-royal-offWhite font-sans overflow-x-hidden w-full relative">
      
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 shadow-sm">
        <div className="container flex h-20 items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-14 w-auto">
               <img src="/assets/logo_RL_.png" alt="Royal Life Logo" className="h-full w-auto object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-serif text-royal-maroon leading-none">ROYAL LIFE</h1>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Steel Furniture Pvt Ltd</p>
            </div>
          </div>

          <div className="hidden md:block">
            <NavLinks setIsMenuOpen={setIsMenuOpen} />
          </div>

          <div className="hidden md:flex gap-3">
             <Button
              variant="outline"
              className="border-royal-maroon text-royal-maroon hover:bg-royal-maroon hover:text-white"
              onClick={() => handleWhatsApp("Hi, I found you on www.royalsteelfurniture.in. I have a query.")}>

                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
             </Button>
             <Button
              className="bg-royal-maroon hover:bg-royal-lightMaroon text-white"
              onClick={handleCall}>

                <Phone className="mr-2 h-4 w-4" /> {CONTACT_NUMBER}
             </Button>
          </div>

          <div className="md:hidden">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6 text-royal-maroon" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-royal-beige/50 backdrop-blur-xl border-l-royal-maroon/20">
                <div className="flex flex-col gap-8 mt-10">
                  <NavLinks mobile setIsMenuOpen={setIsMenuOpen} />
                  <div className="flex flex-col gap-3">
                    <Button className="w-full bg-royal-maroon" onClick={handleCall}>
                      <Phone className="mr-2 h-4 w-4" /> Call Now
                    </Button>
                    <Button variant="outline" className="w-full border-royal-maroon text-royal-maroon" onClick={() => handleWhatsApp()}>
                      <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-royal-offWhite via-white to-royal-beige pt-8 pb-12 lg:pt-32 lg:pb-24">
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 lg:space-y-8 animate-in slide-in-from-left duration-700">
              <div className="inline-flex items-center rounded-full border border-royal-maroon/20 bg-royal-beige/30 px-3 py-1 text-sm text-royal-maroon">
                <span className="flex h-2 w-2 rounded-full bg-royal-maroon mr-2"></span>
                30+ Years of Excellence
              </div>
              <h1 className="text-4xl lg:text-6xl font-serif font-bold text-gray-900 leading-tight">
                Premium <span className="text-royal-maroon">Steel Furniture</span> for Every Space
              </h1>
              <p className="max-w-lg !text-lg text-gray-600">
                Experience the perfect blend of durability and elegance. Trusted by 300+ dealers and 3 Million+ happy customers across Central India.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                 <Button size="lg" className="bg-royal-maroon hover:bg-royal-lightMaroon text-white h-12 px-8 text-lg" onClick={handleCall}>
                    Call Now
                 </Button>
                 <Button size="lg" variant="outline" className="border-gray-700 bg-gray-800/80 text-white h-12 px-8 text-base uppercase tracking-widest hover:bg-gray-700 gap-3" onClick={() => handleWhatsApp("Hi, I found you on www.royalsteelfurniture.in. I am interested in your furniture.")}>
                    Enquire on <svg viewBox="0 0 24 24" className="h-5 w-5 fill-green-500"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                 </Button>
              </div>
              <div className="flex items-center gap-6 pt-4 text-sm text-gray-500 font-medium">
                <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4 text-royal-maroon" /> ISO Certified</span>
                <span className="flex items-center gap-1"><Factory className="h-4 w-4 text-royal-maroon" /> In-house Manufacturer</span>
              </div>
            </div>
            
            <div className="relative animate-in slide-in-from-right duration-700 delay-200 mt-8 lg:mt-0">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                <Carousel setApi={setApi} className="w-full" opts={HERO_CAROUSEL_OPTS}>
                  <CarouselContent>
                    {HERO_IMAGES.map((img, index) =>
                    <CarouselItem key={`hero-${img}`}>
                        <img
                        src={img}
                        alt={`Manufacturing Process ${index + 1}`}
                        fetchpriority={index === 0 ? "high" : "low"}
                        loading={index === 0 ? "eager" : "lazy"}
                        className="w-full h-[300px] lg:h-[500px] object-cover" />

                      </CarouselItem>
                    )}
                  </CarouselContent>
                </Carousel>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white pointer-events-none">
                   <p className="font-serif text-lg">State-of-the-art Manufacturing</p>
                   <p className="text-sm opacity-80">CNC Precision • Italian Hinges</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-royal-maroon text-white py-12">
         <div className="container grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
               <Calendar className="mx-auto h-8 w-8 text-royal-beige" />
               <h3 className="text-3xl font-bold font-serif">30+</h3>
               <p className="text-sm opacity-80">Years Experience</p>
            </div>
            <div className="space-y-2">
               <ShieldCheck className="mx-auto h-8 w-8 text-royal-beige" />
               <h3 className="text-3xl font-bold font-serif">300+</h3>
               <p className="text-sm opacity-80">Trusted Dealers</p>
            </div>
            <div className="space-y-2">
               <Users className="mx-auto h-8 w-8 text-royal-beige" />
               <h3 className="!font-serif !font-bold !text-3xl">3 Million+</h3>
               <p className="text-sm opacity-80">Happy Customers</p>
            </div>
         </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 md:py-20 bg-white">
        <div className="container max-w-4xl text-center space-y-8">
           <span className="text-royal-maroon font-semibold tracking-wider text-sm uppercase">Our Story</span>
           <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900">
              Trusted Name in Central India Since 1993
           </h2>
           <p className="text-gray-600 text-lg leading-relaxed">
              Royal Life Furniture has been an integral part of Indian households and workplaces for over three decades. 
              Known for durability and safety, we use state-of-the-art technologies like CNC machines and automated powder coating 
              to produce weather-resistant, scratch-resistant furniture.
           </p>
           <div className="flex flex-wrap justify-center items-center gap-8 mt-8">
              {CERT_LOGOS.map((logo, i) =>
            <div key={`cert-${logo}`} className="h-20 w-auto bg-white p-2 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
                    <img src={logo} alt={`Certification ${i + 1}`} className="h-full w-auto object-contain" />
                 </div>
            )}
           </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-12 md:py-20 bg-royal-offWhite">
         <div className="container">
            <div className="text-center mb-10 space-y-4">
               <h2 className="text-4xl font-serif font-bold text-gray-900">Our Premium Collection</h2>
               <p className="text-gray-600 max-w-2xl mx-auto">
                  Browse our extensive range of high-quality furniture designed for your needs.
               </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-col items-center gap-6 mb-12">
               <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
                  <div className="flex gap-3 px-2 min-w-max justify-start md:justify-center">
                  {categories.map((cat) =>
              <Button
                key={cat.id}
                size="lg"
                onClick={() => {setActiveTab(cat.slug);setShowAll(false);}}
                className={`rounded-full px-6 text-sm md:text-lg whitespace-nowrap transition-all duration-300 ${
                activeTab === cat.slug ?
                'bg-royal-maroon text-white shadow-lg scale-105' :
                'bg-white text-gray-600 hover:bg-gray-100'}`
                }>

                        {cat.name}
                      </Button>
              )}
                  </div>
               </div>

               {/* Download Brochure Button */}
               {categories.find(c => c.slug === activeTab)?.pdf_url && (
               <Button
              variant="outline"
              className="border-royal-maroon text-royal-maroon hover:bg-royal-maroon hover:text-white transition-colors gap-2"
              onClick={() => {
                trackClick('brochure_dl');
                const cat = categories.find(c => c.slug === activeTab);
                if (cat?.pdf_url) window.open(cat.pdf_url, '_blank', 'noopener,noreferrer');
              }}>

                  <Download className="h-4 w-4" />
                  Download {categories.find(c => c.slug === activeTab)?.name} Catalogue (PDF)
               </Button>
               )}
            </div>

            {/* Products Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 min-h-[400px]">
               {displayProducts.length === 0 ? (
                   <div className="col-span-full text-center py-20 text-gray-400">
                     <p className="text-lg font-medium">No products in this category yet</p>
                     <p className="text-sm mt-2">Products coming soon! Contact us for more details.</p>
                   </div>
               ) : (
                   displayProducts.map((product) =>
                  <Card key={product.id} className="group overflow-hidden border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                     <div className="relative h-72 overflow-hidden bg-gray-50">
                        <img
                  src={product.image}
                  alt={product.title}
                  loading="lazy"
                  className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" />

                        {product.tag &&
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-royal-maroon uppercase tracking-wide">
                            {product.tag}
                            </div>
                }
                     </div>
                     <CardHeader>
                        <p className="text-sm text-royal-maroon font-medium">{categories.find(c => c.slug === product.category_slug)?.name || product.category_slug}</p>
                        <CardTitle className="font-serif text-xl">{product.title}</CardTitle>
                     </CardHeader>
                     <CardContent>
                        {product.dims && <p className="text-sm text-gray-500 mb-4">Dimensions: {product.dims}</p>}
                        {product.features.length > 0 && (
                        <ul className="space-y-2">
                           {product.features.map((feat, idx) =>
                  <li key={`${product.id}-feat-${feat}`} className="flex items-center text-sm text-gray-600">
                                 <span className="w-1.5 h-1.5 rounded-full bg-royal-lightMaroon mr-2"></span>
                                 {feat}
                              </li>
                  )}
                        </ul>
                        )}
                     </CardContent>
                     <CardFooter className="pt-0">
                        <Button className="w-full bg-gray-900 hover:bg-royal-maroon transition-colors" onClick={() => handleWhatsApp(`Hi, I found you on www.royalsteelfurniture.in. I am interested in ${product.title}`)}>
                           <MessageCircle className="mr-2 h-4 w-4" /> Get Quote
                        </Button>
                     </CardFooter>
                  </Card>
            ))}
            </div>

            {/* View More Button */}
            {filteredProducts.length > 6 &&
          <div className="text-center mt-12">
                  <Button
              variant="outline"
              size="lg"
              onClick={() => setShowAll(!showAll)}
              className="border-royal-maroon text-royal-maroon hover:bg-royal-beige/50 gap-2">

                     {showAll ?
              <>Show Less <ChevronUp className="h-4 w-4" /></> :

              <>View All {filteredProducts.length} Products <ChevronDown className="h-4 w-4" /></>
              }
                  </Button>
               </div>
          }
         </div>
      </section>

      {/* YouTube Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container text-center">
           <div className="flex flex-col items-center justify-center space-y-4 mb-12">
              <div className="p-3 bg-red-100 rounded-full text-red-600">
                <Youtube className="w-8 h-8" />
              </div>
              <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900">
                 Watch Us in Action
              </h2>
              <p className="text-gray-600 max-w-2xl text-lg">
                 See our manufacturing process and product durability tests on our official YouTube channel.
              </p>
           </div>

           <div className="mb-12">
             <Carousel className="w-full max-w-5xl mx-auto" opts={VIDEO_CAROUSEL_OPTS}>
               <CarouselContent className="-ml-4">
                 {FEATURED_VIDEOS.map((video, index) =>
                <CarouselItem key={`video-${video}`} className="pl-4 md:basis-1/2 lg:basis-1/3">
                     <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-white bg-gray-900 aspect-[9/16] relative group">
                        <video
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      preload="metadata">

                           <source src={video} type="video/mp4" />
                           Your browser does not support the video tag.
                        </video>
                     </div>
                   </CarouselItem>
                )}
               </CarouselContent>
               <CarouselPrevious className="flex -left-4 md:-left-12 bg-royal-maroon text-white hover:bg-royal-lightMaroon hover:text-white border-none scale-75 md:scale-100" />
               <CarouselNext className="flex -right-4 md:-right-12 bg-royal-maroon text-white hover:bg-royal-lightMaroon hover:text-white border-none scale-75 md:scale-100" />
             </Carousel>
           </div>

           <Button 
              size="lg" 
              className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-red-700 h-14 !font-medium !text-lg gap-2 px-8 rounded-full shadow text-white bg-red-600"
              onClick={() => window.open(YOUTUBE_LINK, '_blank', 'noopener,noreferrer')}
           >
              <Youtube className="w-5 h-5" /> Visit Our YouTube Channel
           </Button>
      {/* Sticky Social Sidebar - Always Visible */}
      <div 
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[1000] flex flex-col gap-2 md:gap-3 p-3 bg-transparent"
      >
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-green-500 flex items-center justify-center text-white hover:scale-110 hover:shadow-lg transition-all shadow-md">
              <MessageCircle className="h-4 w-4 md:h-5 md:w-5" />
          </a>
          <a href="https://www.instagram.com/royal_life_steel_furniture/" target="_blank" rel="noopener noreferrer" className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 flex items-center justify-center text-white hover:scale-110 hover:shadow-lg transition-all shadow-md">
              <Instagram className="h-4 w-4 md:h-5 md:w-5" />
          </a>
          <a href="https://www.facebook.com/share/1KYwcgYwMp/" target="_blank" rel="noopener noreferrer" className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:scale-110 hover:shadow-lg transition-all shadow-md">
              <Facebook className="h-4 w-4 md:h-5 md:w-5" />
          </a>
          <a href={YOUTUBE_LINK} target="_blank" rel="noopener noreferrer" className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-red-600 flex items-center justify-center text-white hover:scale-110 hover:shadow-lg transition-all shadow-md">
              <Youtube className="h-4 w-4 md:h-5 md:w-5" />
          </a>
      </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-royal-maroon text-white relative overflow-hidden">
         <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
         
         <div className="container relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
               <div className="space-y-8">
                  <h2 className="text-4xl font-serif font-bold">Why Choose Royal Life?</h2>
                  <div className="space-y-6">
                     {[
                { title: "Unmatched Durability", desc: "Built with high-gauge steel and automated powder coating for rust resistance." },
                { title: "Italian Craftsmanship", desc: "Featuring premium Italian hinges and smooth operating mechanisms." },
                { title: "Client-Centric Approach", desc: "Total quality management with timely delivery and ethical practices." },
                { title: "Wide Network", desc: "Strong presence across Maharashtra, AP, MP, Chhattisgarh and Telangana." }].
                map((item, i) =>
                <div key={item.title} className="flex gap-4">
                           <div className="h-12 w-12 rounded-full bg-royal-beige/20 flex items-center justify-center shrink-0">
                              <CheckCircle className="h-6 w-6 text-royal-beige" />
                           </div>
                           <div>
                              <h3 className="text-xl font-semibold mb-1">{item.title}</h3>
                              <p className="text-royal-beige/80">{item.desc}</p>
                           </div>
                        </div>
                )}
                  </div>
               </div>
               
               <div className="bg-white text-gray-900 rounded-2xl p-8 shadow-2xl">
                  <h3 className="text-2xl font-serif font-bold text-royal-maroon mb-4">Become a Dealer</h3>
                  <p className="text-gray-600 mb-6">
                     Join our network of over 300+ dealers. We supply to GEM, government, and institutional projects.
                  </p>
                  <ul className="space-y-3 mb-8">
                     <li className="flex items-center text-sm font-medium"><CheckCircle className="mr-2 h-4 w-4 text-royal-maroon" /> Special Dealer Pricing</li>
                     <li className="flex items-center text-sm font-medium"><CheckCircle className="mr-2 h-4 w-4 text-royal-maroon" /> Marketing Support</li>
                     <li className="flex items-center text-sm font-medium"><CheckCircle className="mr-2 h-4 w-4 text-royal-maroon" /> Priority Delivery</li>
                  </ul>
                  <Button className="w-full h-12 text-lg bg-royal-maroon hover:bg-royal-lightMaroon text-white" onClick={handleCall}>
                     Call for Dealership
                  </Button>
               </div>
            </div>
         </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 md:py-24 bg-royal-offWhite">
         <div className="container">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
               <div className="space-y-8">
                  <div>
                     <span className="text-royal-maroon font-semibold tracking-wider text-sm uppercase">Get in Touch</span>
                     <h2 className="text-4xl font-serif font-bold text-gray-900 mt-2">Visit Our Factory</h2>
                  </div>
                  
                  <div className="space-y-6">
                     <div className="flex items-start gap-4">
                        <MapPin className="h-6 w-6 text-royal-maroon shrink-0 mt-1" />
                        <div>
                           <h4 className="font-semibold text-gray-900">Factory Address</h4>
                           <p className="text-gray-600 break-words pr-4">Royal Life Steel Furniture Pvt Ltd Near S. T. Workshop , Dalda Factory Square, New Cotton Market Rd, Ghat Road, Nagpur, Maharashtra 440018</p>
                        </div>
                     </div>
                     <div className="flex items-start gap-4">
                        <Users className="h-6 w-6 text-royal-maroon shrink-0 mt-1" />
                        <div>
                           <h4 className="font-semibold text-gray-900">Contact Person</h4>
                           <p className="text-gray-600">Office</p>
                        </div>
                     </div>
                     <div className="flex items-start gap-4">
                        <Phone className="h-6 w-6 text-royal-maroon shrink-0 mt-1" />
                        <div>
                           <h4 className="font-semibold text-gray-900">Phone & WhatsApp</h4>
                           <p className="text-gray-600 font-mono text-lg">{CONTACT_NUMBER}</p>
                           <p className="text-gray-600 font-mono text-lg">+91 99235 32545</p>
                           <p className="!font-mono !text-lg text-gray-600">+91 78758 86747</p>
                           <p className="!font-semibold !text-sm !text-left mt-2 !shadow-sm text-royal-maroon !bg-[rgba(255,255,255,0.95)]">Customization available only on bulk orders

                    </p>
                        </div>
                     </div>
                  </div>

                  <div className="w-full h-64 bg-gray-200 rounded-lg overflow-hidden border border-gray-300 shadow-inner">
                    <iframe
                  title="Royal Industries Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3721.282869874839!2d79.099!3d21.141!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4c0c169229f6b%3A0x62953043818e6b12!2sGhat%20Rd%2C%20Nagpur%2C%20Maharashtra%20440018!5e0!3m2!1sen!2sin!4v1629283748293!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy">
                </iframe>
                  </div>
               </div>

               <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6">Send us an Enquiry</h3>
                  <form onSubmit={handleFormSubmit} className="space-y-6">
                     <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Your Name</label>
                        <Input
                    required
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-12" />

                     </div>
                     <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Phone Number</label>
                        <Input
                    required
                    type="tel"
                    placeholder="Enter your mobile number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-12" />

                     </div>
                     <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Message</label>
                        <Textarea
                    placeholder="Which product are you interested in?"
                    className="min-h-[120px]"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })} />

                     </div>
                     <Button type="submit" className="w-full h-12 text-lg bg-royal-maroon hover:bg-royal-lightMaroon text-white" disabled={loading}>
                        {loading ? "Processing..." : "Submit & Start WhatsApp Chat"}
                     </Button>
                     <p className="text-xs text-center text-gray-500 mt-4">
                        We respect your privacy. Your details are safe with us.<br />
                        By submitting, you agree to our <a href="/privacy-policy" className="text-royal-maroon underline">Privacy Policy</a> and <a href="/terms-of-service" className="text-royal-maroon underline">Terms of Service</a>.
                     </p>
                  </form>
               </div>
            </div>
         </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 border-t border-gray-800">
         <div className="container">
            <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-8">
               <div>
                  <h2 className="text-2xl font-bold font-serif text-white">ROYAL LIFE</h2>
                  <p className="text-sm text-gray-500">Steel Furniture Pvt Ltd</p>
               </div>
               <div className="flex gap-8 text-sm font-medium">
                  <a href="#about" className="hover:text-white transition-colors">About</a>
                  <a href="#products" className="hover:text-white transition-colors">Products</a>
                  <a href="#contact" className="hover:text-white transition-colors">Contact</a>
               </div>
               <div className="flex gap-4">
                  <a href="https://www.instagram.com/royal_life_steel_furniture/" target="_blank" rel="noopener noreferrer" className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-royal-maroon transition-colors cursor-pointer text-white">
                     <Instagram className="h-5 w-5" />
                  </a>
                  <a href="https://www.facebook.com/share/1KYwcgYwMp/" target="_blank" rel="noopener noreferrer" className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-royal-maroon transition-colors cursor-pointer text-white">
                     <Facebook className="h-5 w-5" />
                  </a>
                  <a href={YOUTUBE_LINK} target="_blank" rel="noopener noreferrer" className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer text-white">
                     <Youtube className="h-5 w-5" />
                  </a>
               </div>
            </div>
            <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-500">
               <p>&copy; {new Date().getFullYear()} Royal Life Furniture. All rights reserved.</p>
               <p className="mt-2">GST: 27AADCR9622B1ZI</p>
               <div className="flex justify-center gap-4 mt-3">
                  <a href="/privacy-policy" className="hover:text-white transition-colors underline" data-testid="footer-privacy-link">Privacy Policy</a>
                  <span>|</span>
                  <a href="/terms-of-service" className="hover:text-white transition-colors underline" data-testid="footer-terms-link">Terms of Service</a>
               </div>
            </div>
         </div>
      </footer>
    </div>);

}